export const SUPABASE_STORAGE_PROVIDER = 'supabase-storage'
export const PUBLIC_MEDIA_BUCKET = 'public-media'

export const IMAGE_SURFACES = {
  profiles: 'profiles',
  characters: 'characters',
  personas: 'personas',
  worlds: 'worlds',
  openWorldSetups: 'open-world-setups',
} as const

export type ImageSurface = typeof IMAGE_SURFACES[keyof typeof IMAGE_SURFACES]

export interface ImageFocus {
  x: number
  y: number
}

export const DEFAULT_IMAGE_FOCUS: ImageFocus = { x: 50, y: 50 }

export interface ImageAssetInput {
  provider: string
  bucket: string
  path: string
  public_url: string
  mime_type: string
  bytes: number | null
  width: number | null
  height: number | null
  original_name: string
}

export interface AppImage {
  url: string
  focus: ImageFocus | null
  asset: ImageAssetInput | null
}

interface AppImageFallback {
  url?: unknown
  focus?: unknown
  asset?: unknown
}

interface ImageFieldConfig {
  urlField: string
  providerField: string
  bucketField: string
  pathField: string
  mimeTypeField: string
  bytesField: string
  widthField: string
  heightField: string
  originalNameField: string
  focusXField: string
  focusYField: string
}

const IMAGE_FIELD_CONFIG: Record<string, ImageFieldConfig> = {
  avatar: {
    urlField: 'avatar_url',
    providerField: 'avatar_provider',
    bucketField: 'avatar_bucket',
    pathField: 'avatar_storage_path',
    mimeTypeField: 'avatar_mime_type',
    bytesField: 'avatar_bytes',
    widthField: 'avatar_width',
    heightField: 'avatar_height',
    originalNameField: 'avatar_original_name',
    focusXField: 'avatar_focus_x',
    focusYField: 'avatar_focus_y',
  },
  img: {
    urlField: 'imgpath',
    providerField: 'img_provider',
    bucketField: 'img_bucket',
    pathField: 'img_storage_path',
    mimeTypeField: 'img_mime_type',
    bytesField: 'img_bytes',
    widthField: 'img_width',
    heightField: 'img_height',
    originalNameField: 'img_original_name',
    focusXField: 'img_focus_x',
    focusYField: 'img_focus_y',
  },
}

function getFieldConfig(prefix: string): ImageFieldConfig {
  const config = IMAGE_FIELD_CONFIG[prefix]
  if (!config) {
    throw new Error(`[imageAssetFields] Unsupported image field prefix: ${prefix}`)
  }
  return config
}

function normalizeString(value: unknown): string {
  return value == null ? '' : String(value).trim()
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return Boolean(value) && typeof value === 'object' && !Array.isArray(value)
}

function normalizeNumber(value: unknown): number | null {
  if (value === null || value === undefined || value === '') return null
  const numeric = Number(value)
  return Number.isFinite(numeric) && numeric >= 0 ? numeric : null
}

function clampPercent(value: unknown, fallback: number): number {
  const numeric = Number(value)
  if (!Number.isFinite(numeric)) return fallback
  return Math.min(100, Math.max(0, numeric))
}

export function normalizeImageFocus(value: unknown, fallback: ImageFocus = DEFAULT_IMAGE_FOCUS): ImageFocus {
  if (!value || typeof value !== 'object') {
    return { ...fallback }
  }
  const focus = value as Record<string, unknown>
  return {
    x: clampPercent(focus.x, fallback.x),
    y: clampPercent(focus.y, fallback.y),
  }
}

export function sameImageFocus(a: unknown, b: unknown): boolean {
  const left = normalizeImageFocus(a)
  const right = normalizeImageFocus(b)
  return left.x === right.x && left.y === right.y
}

export function imageFocusToObjectPosition(value: unknown): string {
  const focus = normalizeImageFocus(value)
  return `${focus.x}% ${focus.y}%`
}

export function normalizeUploadedImageAssetInput(asset: unknown): ImageAssetInput | null {
  if (!asset || typeof asset !== 'object') return null
  const a = asset as Record<string, unknown>
  if (isRecord(a.asset)) {
    return normalizeUploadedImageAssetInput(a.asset)
  }

  const provider = normalizeString(a.provider)
  const bucket = normalizeString(a.bucket)
  const path = normalizeString(a.path)
  const publicUrl = normalizeString(a.public_url)

  if (!provider || !bucket || !path) return null

  return {
    provider,
    bucket,
    path,
    public_url: publicUrl,
    mime_type: normalizeString(a.mime_type),
    bytes: normalizeNumber(a.bytes),
    width: normalizeNumber(a.width),
    height: normalizeNumber(a.height),
    original_name: normalizeString(a.original_name),
  }
}

export function normalizeAppImage(value: unknown, fallback: AppImageFallback = {}): AppImage | null {
  const record = isRecord(value) ? value : {}
  const rawAsset = record.asset ?? record.img_asset ?? record.avatar_asset ?? fallback.asset ?? record
  const asset = normalizeUploadedImageAssetInput(rawAsset)
  const url = normalizeString(
    record.url
    ?? record.public_url
    ?? record.imgpath
    ?? record.avatar_url
    ?? fallback.url
    ?? asset?.public_url,
  )
  const rawFocus = record.focus ?? record.img_focus ?? record.avatar_focus ?? fallback.focus
  const fallbackFocus = fallback.focus === null ? null : normalizeImageFocus(fallback.focus)
  const focus = rawFocus === null
    ? null
    : (rawFocus === undefined ? fallbackFocus : normalizeImageFocus(rawFocus))

  if (!url && !asset) return null

  return {
    url: url || asset?.public_url || '',
    focus,
    asset: asset
      ? {
          ...asset,
          public_url: asset.public_url || url,
        }
      : null,
  }
}

export function buildImageFocusColumns(prefix: string, focusInput: unknown): Record<string, unknown> {
  const config = getFieldConfig(prefix)
  if (focusInput === null) {
    return {
      [config.focusXField]: null,
      [config.focusYField]: null,
    }
  }

  const focus = normalizeImageFocus(focusInput)
  return {
    [config.focusXField]: focus.x,
    [config.focusYField]: focus.y,
  }
}

export function buildImageColumns(prefix: string, assetInput: unknown): Record<string, unknown> {
  const config = getFieldConfig(prefix)
  const asset = normalizeUploadedImageAssetInput(assetInput)

  if (!asset) {
    return {
      [config.urlField]: '',
      [config.providerField]: null,
      [config.bucketField]: null,
      [config.pathField]: null,
      [config.mimeTypeField]: null,
      [config.bytesField]: null,
      [config.widthField]: null,
      [config.heightField]: null,
      [config.originalNameField]: null,
    }
  }

  return {
    [config.urlField]: asset.public_url,
    [config.providerField]: asset.provider,
    [config.bucketField]: asset.bucket,
    [config.pathField]: asset.path,
    [config.mimeTypeField]: asset.mime_type || null,
    [config.bytesField]: asset.bytes,
    [config.widthField]: asset.width,
    [config.heightField]: asset.height,
    [config.originalNameField]: asset.original_name || null,
  }
}

export function getStoredImageFocus(row: Record<string, unknown> | null | undefined, prefix: string): ImageFocus {
  const config = getFieldConfig(prefix)
  return normalizeImageFocus({
    x: row?.[config.focusXField],
    y: row?.[config.focusYField],
  })
}

export function getStoredImageAsset(row: Record<string, unknown> | null | undefined, prefix: string): ImageAssetInput | null {
  const config = getFieldConfig(prefix)
  const provider = normalizeString(row?.[config.providerField])
  const bucket = normalizeString(row?.[config.bucketField])
  const path = normalizeString(row?.[config.pathField])

  if (!provider || !bucket || !path) return null

  return {
    provider,
    bucket,
    path,
    public_url: normalizeString(row?.[config.urlField]),
    mime_type: normalizeString(row?.[config.mimeTypeField]),
    bytes: normalizeNumber(row?.[config.bytesField]),
    width: normalizeNumber(row?.[config.widthField]),
    height: normalizeNumber(row?.[config.heightField]),
    original_name: normalizeString(row?.[config.originalNameField]),
  }
}

export function getStoredAppImage(
  row: Record<string, unknown> | null | undefined,
  prefix: string,
  { fallbackUrl = '', getPublicUrl }: Pick<ResolveStoredImageUrlOptions, 'fallbackUrl' | 'getPublicUrl'> = {},
): AppImage | null {
  const url = resolveStoredImageUrl({ row, prefix, fallbackUrl, getPublicUrl })
  const asset = getStoredImageAsset(row, prefix)
  const focus = getStoredImageFocus(row, prefix)
  return normalizeAppImage({ url, focus, asset })
}

interface ResolveStoredImageUrlOptions {
  row?: Record<string, unknown> | null
  prefix?: string
  fallbackUrl?: string
  getPublicUrl?: (bucket: string, path: string) => string
}

export function resolveStoredImageUrl({ row, prefix = '', fallbackUrl = '', getPublicUrl }: ResolveStoredImageUrlOptions = {}): string {
  const config = getFieldConfig(prefix)
  const directUrl = normalizeString(row?.[config.urlField] ?? fallbackUrl)
  if (directUrl) return directUrl

  const asset = getStoredImageAsset(row ?? null, prefix)
  if (!asset || typeof getPublicUrl !== 'function') return ''
  return normalizeString(getPublicUrl(asset.bucket, asset.path))
}

export function sameStoredImageAsset(a: unknown, b: unknown): boolean {
  const left = normalizeUploadedImageAssetInput(a)
  const right = normalizeUploadedImageAssetInput(b)
  if (!left || !right) return false
  return left.provider === right.provider && left.bucket === right.bucket && left.path === right.path
}

export function isStorageBackedImageAsset(asset: unknown): boolean {
  const normalized = normalizeUploadedImageAssetInput(asset)
  return Boolean(normalized?.bucket && normalized?.path)
}
